<script setup lang="ts">
import AttendanceChatCards from "~/components/Attendance/review/AttendanceChatCards.vue";
import AttendanceConfirmedCard from "~/components/Attendance/review/AttendanceConfirmedCard.vue";
import AppButton from "~/components/Common/Buttons/AppButton.vue";
import CopilotError from "~/components/Copilot/blocks/CopilotError.vue";
import CopilotSuggestions from "~/components/Copilot/blocks/CopilotSuggestions.vue";
import CopilotDropZone from "~/components/Copilot/composer/CopilotDropZone.vue";
import CopilotUrlInput from "~/components/Copilot/composer/CopilotUrlInput.vue";
import CopilotConversation from "~/components/Copilot/conversation/CopilotConversation.vue";
import CopilotMessage from "~/components/Copilot/conversation/CopilotMessage.vue";
import CopilotShell from "~/components/Copilot/conversation/CopilotShell.vue";
import { useCopilotChat } from "~/composables/copilot/useCopilotChat";
import { attendanceRunMessageIdOf } from "~/utils/helpers/attendance/run-envelope";
import { useAttendanceReviewStore } from "~/store/attendance/attendance-review.store";
import { useCopilotPanelStore } from "~/store/copilot/copilot-panel.store";
import { useCreateTrainingStore } from "~/store/trainings/create-training.store";
import { CopilotDocTypeEnum, CopilotRoleEnum } from "~/utils/enums/copilot/chat.enums";

const copilotPanelStore = useCopilotPanelStore();
const attendanceReviewStore = useAttendanceReviewStore();
const createTrainingStore = useCreateTrainingStore();

const shellRef = ref<InstanceType<typeof CopilotShell> | null>(null);
const dropZoneRef = ref<InstanceType<typeof CopilotDropZone> | null>(null);
const urlInputRef = ref<InstanceType<typeof CopilotUrlInput> | null>(null);

const {
  chatId,
  messages,
  draft,
  pendingAttachment,
  programUrl,
  localError,
  isCorrectingUrl,
  isBusy,
  transportError,
  transportOriginatingMessage,
  canRetryTransportError,
  originatingMessageAt,
  canRetryAt,
  openReview,
  onSend,
  onErrorRecovery,
  onRetryAt,
  onFile,
  onRemoveAttachment,
  onAnalyzeUrl,
  onPickAction,
  onApprove,
  onDeny,
  resetConversation,
  stop,
} = useCopilotChat({
  focusUrlInput: () => urlInputRef.value?.focus(),
  openProgramPicker: () => dropZoneRef.value?.openFilePicker(CopilotDocTypeEnum.TRAINING_PROGRAM),
  focusComposer: () => shellRef.value?.focusInput(),
});

const hasAttendanceCards = computed(() => attendanceReviewStore.hasChatCards(isBusy.value));

/**
 * The welcome screen: the question, the drop zone and the example questions, offered only while the
 * conversation is still blank. A pending session choice takes the panel over, so it hides it too.
 */
const isWelcomeVisible = computed(() => !messages.value.length && !attendanceReviewStore.hasSessionChoice);

/**
 * The answer the attendance cards belong to — null while the run came from outside the chat, which
 * is what still earns them a bubble of their own. Past that answer they stop painting, so a reply
 * that declared no run of its own cannot inherit them. Resolved against the run actually on screen:
 * the conversation can still carry an older one the store has since moved off.
 */
const attendanceHostMessageId = computed(() =>
  attendanceRunMessageIdOf(conversationMessages.value, attendanceReviewStore.runId),
);

// The SDK triggers its shallow ref while keeping the same array. Give the conversation a new
// array per update so production's prop checks do not skip rendering streamed content.
const conversationMessages = computed(() => [...messages.value]);
</script>

<template>
  <!--
    assistant-over-modals: `above-modals` floats the panel over a session detail and over the
    training form, so the admin can keep using it there. It stands down for the attendance review,
    which is the focus then, and for the training form's exit confirm, a centred question whose
    buttons it would cover. Standing down rather than closing is what leaves the conversation
    behind it, ready where the admin left it. A pending session choice must NOT stand down: the
    picker renders inside the conversation, so dropping the panel hides the very card to click.
  -->
  <CopilotShell
    v-if="copilotPanelStore.isOpen"
    ref="shellRef"
    v-model="draft"
    title="Simbel AI"
    disclaimer="Les contenus générés par l'IA peuvent être inexacts."
    :busy="isBusy"
    :above-modals="!attendanceReviewStore.isOpen && !createTrainingStore.showExit"
    :attachment-name="pendingAttachment?.file.name ?? null"
    can-attach
    @close="copilotPanelStore.setIsOpen(false)"
    @send="onSend()"
    @stop="stop()"
    @file="onFile"
    @remove-attachment="onRemoveAttachment()"
  >
    <div class="flex flex-col gap-4">
      <!--
        `v-show` rather than `v-if`: `dropZoneRef.openFilePicker` is how the error recovery reopens
        the picker after a URL the assistant could not fetch, and an unmounted zone has no ref.
      -->
      <div v-show="isWelcomeVisible" class="flex flex-col gap-4">
        <span class="heading_3_demi py-3.5 text-center text-primary-color">Comment puis-je vous aider ?</span>

        <CopilotDropZone
          ref="dropZoneRef"
          v-model:program-url="programUrl"
          :disabled="isBusy"
          @file="onFile"
          @analyze-url="onAnalyzeUrl()"
        />

        <!-- The assistant's own bubble: these are its opening words, not a control strip. -->
        <CopilotMessage :role="CopilotRoleEnum.ASSISTANT">
          <CopilotSuggestions :disabled="isBusy" @pick="onPickAction" />
        </CopilotMessage>
      </div>

      <CopilotUrlInput
        v-if="isCorrectingUrl"
        ref="urlInputRef"
        v-model="programUrl"
        :disabled="isBusy"
        @submit="onAnalyzeUrl()"
      />

      <CopilotConversation
        :messages="conversationMessages"
        :chat-id="chatId"
        :busy="isBusy"
        :can-retry-at="canRetryAt"
        :has-extras="hasAttendanceCards"
        :extras-host-message-id="attendanceHostMessageId"
        @pick-action="onPickAction"
        @approve="onApprove"
        @deny="onDeny"
        @open-review="openReview"
        @error-action="
          (recovery, messageId, index) => onErrorRecovery(recovery, messageId, originatingMessageAt(index))
        "
        @retry="onRetryAt"
      >
        <template #extras>
          <AttendanceChatCards :busy="isBusy" />
        </template>
      </CopilotConversation>

      <!-- The recap closes the conversation as an assistant message of its own, bubble included. -->
      <CopilotMessage v-if="attendanceReviewStore.hasChatRecap" :role="CopilotRoleEnum.ASSISTANT">
        <AttendanceConfirmedCard />
      </CopilotMessage>

      <CopilotMessage v-if="localError" :role="CopilotRoleEnum.ASSISTANT">
        <CopilotError :error="localError" :disabled="isBusy" @recover="onErrorRecovery($event, null, null)" />
      </CopilotMessage>

      <!-- The only place a broken round-trip is shown: its own retry, not a second banner's. -->
      <CopilotMessage v-if="transportError" :role="CopilotRoleEnum.ASSISTANT">
        <CopilotError
          :error="transportError"
          :can-retry="canRetryTransportError"
          :disabled="isBusy"
          @recover="onErrorRecovery($event, null, transportOriginatingMessage)"
        />
      </CopilotMessage>
    </div>

    <!-- Stopping now lives on the composer's own button, so only the reset stays here. -->
    <template #footer-top>
      <div v-if="(messages.length || attendanceReviewStore.hasRunOnScreen) && !isBusy" class="flex w-full justify-end">
        <AppButton type="link" icon="add" label="Nouvelle discussion" @click="resetConversation()" />
      </div>
    </template>
  </CopilotShell>
</template>
